// ─────────────────────────────────────────────────────────────────────────
// backNav — "take me back to where I actually was".
//
// THE BUG THIS FIXES
// Every edit page in the consuming app passes a `cancelPath` naming the module
// LIST ("/candidates", "/jobs", "/employers", …). EditFormV1 preferred that
// path over history, so opening Edit *from a detail view* and cancelling threw
// the user out to the list — losing the record they were looking at, its tab,
// its scroll position and any filter behind it.
//
// THE RULE
// History wins. `cancelPath` is demoted to what it is genuinely good for: a
// fallback for a COLD entry (a deep-linked /candidate/edit/:id opened in a
// fresh tab), where navigate(-1) would walk out of the application entirely.
//
// A caller that really must force a destination can still say so explicitly
// with `cancelPathPriority` — but that is now an opt-in exception rather than
// the accidental default.
//
// Nothing here knows a module, a route or a project: it only answers
// "is there in-app history behind me?".
// ─────────────────────────────────────────────────────────────────────────

/**
 * hasInAppHistory — is there a previous entry belonging to THIS app?
 *
 * react-router (v6, history v5) stamps a monotonically increasing `idx` on
 * window.history.state for every entry it pushes. `idx > 0` therefore means
 * "the app itself navigated here", i.e. going back lands on one of our own
 * screens rather than on whatever the tab showed before.
 *
 * `window.history.length` is deliberately NOT used: it counts entries from
 * before the app was loaded, so a fresh tab opened from a bookmark can report
 * a length of 2+ and send the user out to an unrelated site.
 */
export function hasInAppHistory(win = typeof window !== 'undefined' ? window : undefined) {
  const idx = win?.history?.state?.idx;
  return typeof idx === 'number' && idx > 0;
}

/**
 * resolveCancelTarget — what a Cancel/Back control should do.
 *
 * Returns either { back: true } (call navigate(-1)) or { path } (call
 * navigate(path)), so the caller stays in charge of the actual navigation and
 * this module stays router-agnostic and testable.
 *
 * @param {object}  opts
 * @param {string} [opts.cancelPath]          fallback route for a cold entry
 * @param {boolean}[opts.cancelPathPriority]  force cancelPath over history
 * @param {string} [opts.fallbackPath]        last resort when there is neither
 * @param {Window} [opts.win]                 injectable for tests
 */
export function resolveCancelTarget({
  cancelPath,
  cancelPathPriority = false,
  fallbackPath = '/',
  win = typeof window !== 'undefined' ? window : undefined,
} = {}) {
  if (cancelPathPriority && cancelPath) return { path: cancelPath };
  if (hasInAppHistory(win)) return { back: true };
  if (cancelPath) return { path: cancelPath };
  return { path: fallbackPath };
}

/**
 * goBackOrTo — the one-liner most call sites want. Applies
 * resolveCancelTarget with the caller's `navigate`.
 */
export function goBackOrTo(navigate, opts = {}) {
  const target = resolveCancelTarget(opts);
  if (target.back) navigate(-1);
  else navigate(target.path);
  return target;
}
